/**
 * Every request the Activity makes has to go through Discord's proxy, which
 * lives behind a `/.proxy` prefix on `<app-id>.discordsays.com`. In a normal
 * browser tab there is no prefix. Working that out once here keeps the rest of
 * the app free of the distinction.
 */

export const IN_DISCORD = /(^|\.)discordsays\.com$/.test(window.location.hostname);

export const BASE_PATH = IN_DISCORD ? "/.proxy" : "";

export function apiUrl(path: string): string {
  return `${BASE_PATH}${path}`;
}

/** Map artwork, relayed and cached by our own server. */
export function assetUrl(assetPath: string): string {
  return `${BASE_PATH}/api/asset/${assetPath}`;
}

export function socketUrl(session: string, instanceId: string): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const params = new URLSearchParams({ session, instance: instanceId });
  return `${protocol}//${window.location.host}${BASE_PATH}/ws?${params}`;
}

/**
 * Carries the server's message key so the UI can show the failure in the
 * reader's own language. The server's own text rides along as the fallback for
 * anything the catalogue does not know.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const detail = (await response.json().catch(() => null)) as
      | { error?: string; code?: string }
      | null;
    throw new ApiError(
      detail?.error ?? `Request failed with status ${response.status}`,
      detail?.code,
    );
  }
  return (await response.json()) as T;
}

export async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(apiUrl(path));
  if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
  return (await response.json()) as T;
}
